/*
 * treat-video-carousel.js
 * Treat — product video carousel (sections/treat-product-videos.liquid), 2026-10-11.
 *
 * - Native horizontal scroll with snap: swipe on touch, drag with the mouse on desktop,
 *   arrows step one tile, arrow keys work when the row has focus.
 * - Progress line + 01 / 05 counter follow the scroll, in both reading directions.
 * - Shopify-hosted videos play muted and looping while at least 60% on screen, and pause when
 *   they leave. No autoplay with prefers-reduced-motion or when the editor turns it off.
 * - A tap on a tile turns its sound on (and silences any other); a second tap turns it off.
 */
if (!customElements.get('treat-video-carousel')) {
  class TreatVideoCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.treat-vc__track');
      if (!this.track) return;

      this.slides = Array.from(this.track.children);
      this.prevButton = this.querySelector('[data-vc-prev]');
      this.nextButton = this.querySelector('[data-vc-next]');
      this.currentLabel = this.querySelector('[data-vc-current]');
      this.isRtl = getComputedStyle(this).direction === 'rtl';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplay = this.hasAttribute('data-autoplay') && !this.reducedMotion;
      this.labelSoundOn = this.dataset.labelSoundOn || '';
      this.labelSoundOff = this.dataset.labelSoundOff || '';

      this.onScroll = this.onScroll.bind(this);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });

      if (this.prevButton) this.prevButton.addEventListener('click', () => this.step(-1));
      if (this.nextButton) this.nextButton.addEventListener('click', () => this.step(1));

      this.setUpDrag();
      this.setUpVideos();
      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onScroll);
      if (this.observer) this.observer.disconnect();
    }

    /* Distance scrolled from the start edge, always positive (RTL scrollLeft is negative). */
    get position() {
      return Math.abs(this.track.scrollLeft);
    }

    get maxPosition() {
      return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    get stepSize() {
      if (this.slides.length < 2) return this.track.clientWidth;
      const first = this.slides[0].getBoundingClientRect();
      const second = this.slides[1].getBoundingClientRect();
      return Math.abs(second.left - first.left) || first.width;
    }

    step(direction) {
      const delta = direction * this.stepSize * (this.isRtl ? -1 : 1);
      this.track.scrollBy({ left: delta, behavior: this.reducedMotion ? 'auto' : 'smooth' });
    }

    onScroll() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    }

    update() {
      const total = this.slides.length;
      const max = this.maxPosition;
      const position = this.position;
      const index = Math.min(total - 1, Math.round(position / (this.stepSize || 1)));

      /* Counter and line move together: tile 2 of 5 → 02 / 05 and 40%; the end of the row
         always reads as the last tile. */
      const atEnd = max > 0 && position >= max - 2;
      const shown = atEnd ? total : index + 1;
      this.style.setProperty('--treat-vc-progress', `${((shown / total) * 100).toFixed(2)}%`);
      if (this.currentLabel) this.currentLabel.textContent = String(shown).padStart(2, '0');

      if (this.prevButton) this.prevButton.disabled = position <= 2;
      if (this.nextButton) this.nextButton.disabled = max === 0 || position >= max - 2;
    }

    /* Mouse drag to scroll on desktop; touch keeps the native swipe. A drag never triggers
       the tap-for-sound underneath it. */
    setUpDrag() {
      let startX = 0;
      let startScroll = 0;
      let dragging = false;
      let moved = false;

      this.track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        dragging = true;
        moved = false;
        startX = event.clientX;
        startScroll = this.track.scrollLeft;
      });

      window.addEventListener('pointermove', (event) => {
        if (!dragging) return;
        const dx = event.clientX - startX;
        if (!moved && Math.abs(dx) > 5) {
          moved = true;
          this.track.classList.add('is-dragging');
        }
        if (moved) this.track.scrollLeft = startScroll - dx;
      });

      const end = () => {
        if (!dragging) return;
        dragging = false;
        if (!moved) return;
        /* Let the click that follows the drag see the flag, then snap to the nearest tile. */
        this.justDragged = true;
        setTimeout(() => (this.justDragged = false), 0);
        const current = this.position;
        const target = Math.round(current / this.stepSize) * this.stepSize;
        this.track.classList.remove('is-dragging');
        this.track.scrollTo({
          left: (this.isRtl ? -1 : 1) * Math.min(target, this.maxPosition),
          behavior: this.reducedMotion ? 'auto' : 'smooth',
        });
      };

      window.addEventListener('pointerup', end);
      window.addEventListener('pointercancel', end);
      this.track.addEventListener('dragstart', (event) => event.preventDefault());
    }

    setUpVideos() {
      this.tiles = Array.from(this.querySelectorAll('.treat-vc__media')).filter((tile) =>
        tile.querySelector('.treat-vc__video')
      );

      this.tiles.forEach((tile) => {
        const video = tile.querySelector('.treat-vc__video');
        const button = tile.querySelector('.treat-vc__sound');

        video.addEventListener('playing', () => tile.classList.add('is-playing'));
        video.addEventListener('pause', () => tile.classList.remove('is-playing'));

        if (button) {
          button.addEventListener('click', (event) => {
            if (this.justDragged) {
              event.preventDefault();
              return;
            }
            this.toggleSound(tile);
          });
        }
      });

      if (!this.tiles.length || !('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const tile = entry.target;
            const video = tile.querySelector('.treat-vc__video');
            if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
              this.inView.add(tile);
              if (this.autoplay || tile.classList.contains('has-sound')) this.play(video);
            } else {
              this.inView.delete(tile);
              if (!video.paused) video.pause();
            }
          });
        },
        { threshold: [0, 0.6] }
      );

      this.inView = new Set();
      this.tiles.forEach((tile) => this.observer.observe(tile));

      /* Browsers pause muted video in a background tab; pick up again on return. */
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible') return;
        this.inView.forEach((tile) => {
          if (this.autoplay || tile.classList.contains('has-sound')) this.play(tile.querySelector('.treat-vc__video'));
        });
      });
    }

    play(video) {
      const attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') {
        /* Low Power Mode and some browsers refuse autoplay: the tile keeps its play button. */
        attempt.catch(() => video.closest('.treat-vc__media').classList.remove('is-playing'));
      }
    }

    toggleSound(tile) {
      const video = tile.querySelector('.treat-vc__video');
      const button = tile.querySelector('.treat-vc__sound');
      const turnOn = video.paused || video.muted;

      if (turnOn) {
        this.tiles.forEach((other) => {
          if (other === tile) return;
          const otherVideo = other.querySelector('.treat-vc__video');
          otherVideo.muted = true;
          other.classList.remove('has-sound');
          this.setButtonState(other.querySelector('.treat-vc__sound'), false);
          if (!this.autoplay) otherVideo.pause();
        });
        if (video.muted) video.currentTime = 0;
        video.muted = false;
        tile.classList.add('has-sound');
        this.setButtonState(button, true);
        this.play(video);
      } else {
        video.muted = true;
        tile.classList.remove('has-sound');
        this.setButtonState(button, false);
        if (!this.autoplay) video.pause();
      }
    }

    setButtonState(button, soundOn) {
      if (!button) return;
      button.setAttribute('aria-pressed', soundOn ? 'true' : 'false');
      const label = soundOn ? this.labelSoundOff : this.labelSoundOn;
      if (label) button.setAttribute('aria-label', label);
    }
  }

  customElements.define('treat-video-carousel', TreatVideoCarousel);
}
